import {
	IconButton,
	Trigger,
	isSuccess,
	useBulkEdit,
	useDataListColumnFilter,
	usePaginatedList,
	useToasts,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { mapToBulkRequest } from "../../../shared/api/mappings";
import { companyService } from "../shared/api/companyService";
import { AttachContactToInvitationListDrawer } from "../shared/AttachContactsToInvitationListDrawer";
import { IInvitationListAttachBulkEditContactsFormState } from "../shared/types/invitationList";

export const CompanyListBulkActions = () => {
	const { t } = useTranslation();
	const { availableItemIds, selectedRows, setBulkEditing, closeBulkEditing } = useBulkEdit();
	const { activeFilters } = useDataListColumnFilter();
	const { searchParams } = usePaginatedList();
	const { showToast } = useToasts();
	const queryClient = useQueryClient();

	const bulkAttach = useCallback(
		async ({ event, ...values }: IInvitationListAttachBulkEditContactsFormState) => {
			if (!event) {
				throw new Error("Event is missing");
			}
			const response = await companyService.bulkAttach({
				...mapToBulkRequest(selectedRows, searchParams?.search),
				...values,
				eventId: event.id,
				filters: activeFilters ?? null,
			});

			if (isSuccess(response)) {
				showToast({
					title: t("Contacts"),
					body: t("{{contactsCount}} contacts were successfully attached", {
						contactsCount: response.data.data.count,
						count: response.data.data.count,
					}),
					type: "success",
				});
				void queryClient.invalidateQueries({ queryKey: companyService.indexKey });
				setBulkEditing(false);
				closeBulkEditing();
			}
		},
		[activeFilters, closeBulkEditing, queryClient, searchParams?.search, selectedRows, setBulkEditing, showToast, t],
	);

	return (
		<Trigger render={(props) => <AttachContactToInvitationListDrawer {...props} onSubmit={bulkAttach} />}>
			{(props) => (
				<IconButton
					{...props}
					type="button"
					title={t("Add contacts to invitation list")}
					size="small"
					disabled={selectedRows.length === 0 || availableItemIds.length === 0}
					icon="calendar"
				/>
			)}
		</Trigger>
	);
};
